import { useEffect, useState, useCallback } from 'react';
import { CollectionAiCapabilityResult } from '@qkindex/contracts';
import {
  ImageAnalysisAuthority,
  imageAnalysisService,
} from '../services/imageAnalysisService';

const pending = new Map<string, Promise<CollectionAiCapabilityResult>>();
export function useAiAvailability(
  active: boolean,
  authority?: ImageAnalysisAuthority,
) {
  const key =
    active && authority
      ? JSON.stringify([
          authority.userId,
          authority.collectionId,
          authority.membershipVersion,
          authority.compatibilityMode,
        ])
      : '';
  const [state, setState] = useState<{
    key: string;
    capability?: CollectionAiCapabilityResult;
    failed?: boolean;
  }>({ key: '' });
  const [revision, setRevision] = useState(0);
  const refresh = useCallback(() => {
    setState({ key: '' });
    setRevision((value) => value + 1);
  }, []);
  const userId = authority?.userId;
  const collectionId = authority?.collectionId;
  const membershipVersion = authority?.membershipVersion;
  const compatibilityMode = authority?.compatibilityMode;
  useEffect(() => {
    if (!key) {
      setState({ key: '' });
      return;
    }
    if (
      !userId ||
      !collectionId ||
      membershipVersion === undefined ||
      !compatibilityMode
    )
      return;
    let cancelled = false;
    setState({ key });
    let request = pending.get(key);
    if (!request) {
      request = imageAnalysisService.capability({
        userId,
        collectionId,
        membershipVersion,
        compatibilityMode,
      });
      pending.set(key, request);
      void request.then(
        () => pending.delete(key),
        () => pending.delete(key),
      );
    }
    void request.then(
      (capability) => {
        if (!cancelled) setState({ key, capability });
      },
      () => {
        if (!cancelled) setState({ key, failed: true });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [
    key,
    userId,
    collectionId,
    membershipVersion,
    compatibilityMode,
    revision,
  ]);
  const capability = state.key === key ? state.capability : undefined;
  const reason = !key
    ? 'AI keywords are currently unavailable.'
    : state.key === key && state.failed
      ? 'AI availability could not be checked. Reopen this dialog to retry.'
      : !capability
        ? 'Checking AI availability…'
        : capability.reason === 'editor-disabled'
          ? 'The collection owner hasn’t enabled AI for editors and administrators.'
          : capability.reason === 'insufficient-credits'
            ? 'Insufficient AI credits.'
            : capability.reason === 'reconciliation-required'
              ? 'AI credits are temporarily unavailable pending reconciliation.'
              : capability.reason === 'feature-unavailable'
                ? 'AI keywords are currently unavailable.'
                : '';
  return { capability, allowed: capability?.allowed === true, reason, refresh };
}
